import React, { useEffect, useRef, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { BaseComponentProps } from "types";
import "./style.scss";
import {
	EyeOutlined,
	DownloadOutlined,
	ZoomInOutlined,
	ZoomOutOutlined,
	RotateLeftOutlined,
	RotateRightOutlined,
	LeftOutlined,
	RightOutlined,
	UndoOutlined
} from "@ant-design/icons";
import { Modal, Tooltip } from "antd";
import { PhotoItemProps } from "components/upload-multiple-image-dnd";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { translate } from "utils";

interface Props extends BaseComponentProps {
	src: string;
	alt?: string;
	className?: string;
	props?: any;
	width?: number;
	name?: string;
	order?: number;
	photos?: PhotoItemProps[];
}

const ROTATE_ANGLE = 90;
const MAX_SCALE = 5;
const SCALE_STEP = 0.5;
const MAX_SCALE_STEPS = 9;
const RATIO_OVERFLOW = 1.4;
const NEXT_BTN_ID = "image-wrapper-next-btn-id";
const PREVIOUS_BTN_ID = "image-wrapper-previous-btn-id";
const CLOSE_BTN_ID = "image-wrapper-close-btn-id";

const handlePress = (e: KeyboardEvent) => {
	if (e.key === "Escape") {
		document.getElementById(CLOSE_BTN_ID)?.click();
	}
	if (e.key === "ArrowLeft") {
		document.getElementById(PREVIOUS_BTN_ID)?.click();
	}
	if (e.key === "ArrowRight") {
		document.getElementById(NEXT_BTN_ID)?.click();
	}
};

const ImageWrapper: React.FC<Props> = ({ src, alt, width, name, className, props, order, photos  }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [rotateAngle, setRotateAngle] = useState(0);
	const [zoomProperty, _setZoomProperty] = useState(1);
	const [currentOrder, setCurrentOrder] = useState(order);

	const centerBtnRef = useRef<HTMLButtonElement>(null);
	const resetBtnRef = useRef<HTMLButtonElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);
	const zoomPropertyRef = useRef(zoomProperty);

	const setZoomProperty = (value: number) => {
		_setZoomProperty(value);
		zoomPropertyRef.current = value;
	};

	function isTruthy(value?: number) {
		return value === 0 || Boolean(value);
	}

	const handleOpenModal = () => {
		document.body.classList.add("disable-swipe-back");
		setIsModalOpen(true);
	};


	const handleClose = (resetTransform: () => void) => {
		document.body.classList.remove("disable-swipe-back");
		setIsModalOpen(false);
		handleReset(resetTransform);
	};

	const handleReset = (resetTransform: () => void) => {
		if(Math.abs(rotateAngle) === 90) {
			setTimeout(() => {
				resetTransform();
			}, 100);
		}
		resetTransform();
		setRotateAngle(0);
		setZoomProperty(1.0);
	};

	const handleDisplayOverflowPart = () => {
		if(imgRef.current?.width && imgRef.current?.height) {
			if(imgRef.current?.width > imgRef.current?.height) {
				const ratio = imgRef.current?.width / imgRef.current.height;
				if(ratio > RATIO_OVERFLOW) {
					if(Math.abs(rotateAngle) === 90) {
						if(imgRef.current?.height) {
							const marginX = Math.abs(imgRef.current?.height - imgRef.current?.width) * 0.5;
							imgRef.current.style.margin = `${marginX}px ${0}`;
						}
					} else {
						imgRef.current.style.margin = "0";
					}		
				}
			} else {
				if(imgRef.current?.height) {
					const marginY = (imgRef.current?.height-imgRef.current?.width) * 0.5;
					if(Math.abs(rotateAngle) === 90) {
						imgRef.current.style.margin = `0 ${marginY}px`;
					} else {
						imgRef.current.style.margin = "0";
					}
				}
			}
		}
	};

	const handleDownload = () => {
		fetch(photos ? photos[currentOrder || 0].url : src)
			.then(response => response.blob())
			.then(blob => {
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = `${name ?? photos![currentOrder!].path.split("/").pop()}.jpg`;
				link.click();
				window.URL.revokeObjectURL(url);
			});
	};

	const handleRotateRight = () => {
		const newRotateAngle = rotateAngle === 180 ? -ROTATE_ANGLE : rotateAngle + ROTATE_ANGLE;
		setRotateAngle(newRotateAngle);
	};

	const handleRotateLeft = () => {
		const newRotateAngle = rotateAngle === -ROTATE_ANGLE ? 180 : rotateAngle - ROTATE_ANGLE;
		setRotateAngle(newRotateAngle);
	};

	const handlePrevious = () => {
		isTruthy(currentOrder) && setCurrentOrder(currentOrder! - 1);
	};

	const handleNext = () => {
		isTruthy(currentOrder) && setCurrentOrder(currentOrder! + 1);
	};

	const centerImage = () => {
		setTimeout(() => centerBtnRef.current?.click(), 0);
	};

	const handleResize = () => {
		centerImage();
	};

	useEffect(() => {
		if(isModalOpen) {
			handleDisplayOverflowPart();
		}

		if(isModalOpen && centerBtnRef.current) {
			centerImage();
		}

		if(isModalOpen) {
			window.addEventListener("resize", handleResize);
		}

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [isModalOpen]);

	useEffect(() => {
		handleDisplayOverflowPart();
		if(isModalOpen && centerBtnRef.current && currentOrder !== undefined) {
			centerImage();
		}
	}, [currentOrder]);

	useEffect(() => {
		handleDisplayOverflowPart();
		if(isModalOpen && centerBtnRef.current) {
			setTimeout(() => {
				centerImage();
			}, 0);
		}
	}, [rotateAngle]);

	useEffect(() => {
		if(isModalOpen) {
			setCurrentOrder(order);
			document.addEventListener("keyup", handlePress);
		} else {
			setCurrentOrder(0);
			document.removeEventListener("keyup", handlePress);
		}
	}, [isModalOpen]);

	return (
		<>
			{isModalOpen && (
				<Modal
					className="downloadable-modal"
					bodyStyle={{
						height: "100%",
						width: "100%",
						display: "flex",
						alignItems: "center",
						background: "transparent",
						justifyContent: "center",
						padding: 0
					}}
					centered
					closable={false}
					visible={isModalOpen}
					footer={null}
				>
					<>
						{photos && (
							<button
								onClick={handlePrevious}
								disabled={currentOrder === 0}
								id={PREVIOUS_BTN_ID}
							>
								<div
									className={`fixed left-5 top-1/2 -translate-y-1/2 text-white z-10 w-10 h-10 rounded-full bg-black/[0.3] flex items-center justify-center ${
										currentOrder !== 0 && "hover:bg-white/[0.2] hover:text-bold"
									}`}
								>
									<div
										className={`${
											currentOrder === 0 ? "text-slate-400" : "text-white"
										} flex items-center justify-center`}
									>
										<LeftOutlined />
									</div>
								</div>
							</button>
						)}
						{photos && (
							<button
								onClick={handleNext}
								disabled={currentOrder === photos.length - 1}
								id={NEXT_BTN_ID}
							>
								<div
									className={`fixed right-5 top-1/2 -translate-y-1/2 text-white z-10 w-10 h-10 rounded-full bg-black/[0.3] flex items-center justify-center ${
										currentOrder !== photos.length - 1 && "hover:bg-white/[0.2]"
									}`}
								>
									<div
										className={`${
											currentOrder === photos.length - 1
												? "text-slate-400"
												: "text-white"
										} flex items-center justify-center text-bold`}
									>
										<RightOutlined />
									</div>
								</div>
							</button>
						)}

						<TransformWrapper
							initialScale={1}
							maxScale={MAX_SCALE}
							centerOnInit={true}
							smooth={false}
							pinch={{
								disabled: true,
								step: 0,
							}}
							doubleClick={{
								disabled: true,
								step: 0,
							}}
							wheel={{
								disabled: true,
							}}
							disablePadding={true}
						>
							{({ zoomIn, zoomOut, resetTransform, centerView }) => (
								<div className="w-full h-full overflow-auto flex justify-center items-center">
									<div className="absolute top-0 right-0 text-white text-lg flex flex-row-reverse mt-0 pb-3 pt-2 px-3 w-full z-10 bg-black/[.1] items-center">
										<button ref={centerBtnRef} onClick={() => centerView()} />
										<Tooltip placement="bottom" title={translate("Close")}>
											<button
												id={CLOSE_BTN_ID}
												onClick={() => handleClose(resetTransform)}
											>
												<div className="p-3 rounded-full bg-transparent flex items-center justify-center hover:bg-white/[0.2]">
													<CloseOutlined />
												</div>
											</button>
										</Tooltip>
										<Tooltip placement="bottom" title={translate("Download")}>
											<button onClick={handleDownload}>
												<div className="p-3 rounded-full bg-transparent flex items-center justify-center hover:bg-white/[0.2]">
													<DownloadOutlined />
												</div>
											</button>
										</Tooltip>
										<Tooltip placement="bottom" title={translate("Reset")}>
											<button
												ref={resetBtnRef}
												onClick={() => {
													handleReset(resetTransform);
												}}
											>
												<div className="p-3 rounded-full bg-transparent flex items-center justify-center hover:bg-white/[0.2]">
													<UndoOutlined />
												</div>
											</button>
										</Tooltip>
										<Tooltip placement="bottom" title={translate("Zoom out")}>
											<button
												onClick={() => {
													zoomOut(SCALE_STEP);
													setZoomProperty(zoomProperty - 1);
												}}
												disabled={zoomProperty === 1}
												className={
													zoomProperty === 1 ? "text-slate-400" : "text-white"
												}
											>
												<div className="p-3 rounded-full bg-transparent flex items-center justify-center hover:bg-white/[0.2]">
													<ZoomOutOutlined />
												</div>
											</button>
										</Tooltip>
										<Tooltip placement="bottom" title={translate("Zoom in")}>
											<button
												onClick={() => {
													zoomIn(SCALE_STEP);
													setZoomProperty(zoomProperty + 1);
												}}
												disabled={zoomProperty === MAX_SCALE_STEPS}
												className={
													zoomProperty === MAX_SCALE_STEPS
														? "text-slate-400"
														: "text-white"
												}
											>
												<div className="p-3 rounded-full bg-transparent flex items-center justify-center hover:bg-white/[0.2]">
													<ZoomInOutlined />
												</div>
											</button>
										</Tooltip>
										<Tooltip
											placement="bottom"
											title={translate("Rotate right")}
										>
											<button onClick={handleRotateRight}>
												<div className="p-3 rounded-full bg-transparent flex items-center justify-center hover:bg-white/[0.2]">
													<RotateRightOutlined />
												</div>
											</button>
										</Tooltip>
										<Tooltip
											placement="bottom"
											title={translate("Rotate left")}
										>
											<button onClick={handleRotateLeft}>
												<div className="p-3 rounded-full bg-transparent flex items-center justify-center hover:bg-white/[0.2]">
													<RotateLeftOutlined />
												</div>
											</button>
										</Tooltip>
										{currentOrder !== undefined && photos && (
											<p className="absolute left-1/2 -translate-x-1/2 text-sm order-text font-normal">{`${
												currentOrder + 1
											} / ${photos.length}`}</p>
										)}
									</div>
									<TransformComponent>
										<img
											style={{
												transform: `rotate(${rotateAngle}deg)`,
												maxWidth: "500px",
												maxHeight: "500px",
											}}
											src={
												photos !== undefined
													? currentOrder && photos[currentOrder]
														? photos[currentOrder]?.url
														: photos[Math.max(currentOrder! - 1, 0)]?.url
													: src
											}
											alt={alt}
											ref={imgRef}
										/>
									</TransformComponent>
								</div>
							)}
						</TransformWrapper>
					</>
				</Modal>
			)}
			<div className="image-wrapper" onClick={handleOpenModal}>
				<img
					src={src}
					alt={alt}
					width={width}
					{...props}
					className={`${className ?? ""}`}
				/>
				<EyeOutlined className="hover-content" />
			</div>
		</>
	);
};

export default ImageWrapper;